import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import debounce from 'lodash.debounce';
import { always, compose, cond, equals, T, path } from 'ramda';

import { Overlay, ScrollContainer, Content } from './authOverlay.styles';
import { RegisterForm } from '../registerForm';
import { LoginForm } from '../loginForm';
import {
  AuthModalTypes,
  LOGIN_TAB,
  REGISTER_TAB,
  RESET_PASSWORD_TAB,
  RESET_PASSWORD_TAB_SUCCESS,
} from '../../../modules/auth/auth.constants';
import { FADE_IN_UP_TRANSITION_TOTAL_DURATION, TRANSITION_TOTAL_DURATION } from '../animated/animated.constants';
import { BodyScrollLock } from '../bodyScrollLock';
import { GTMEvents } from '../../../modules/analytics';
import { ResetPasswordForm } from '../resetPasswordForm';
import { ResetPasswordFormSuccess } from '../resetPasswordForm/resetPasswordFormSuccess';

export class AuthOverlayComponent extends PureComponent {
  static propTypes = {
    visibleModal: PropTypes.oneOf([...AuthModalTypes, false]),
    hide: PropTypes.func,
    show: PropTypes.func,
    logGTMEvent: PropTypes.func,
  };

  state = {
    scrollable: false,
    visibleModal: null,
    submittedResetPasswordEmail: '',
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  static getDerivedStateFromProps(props) {
    if (props.visibleModal) {
      return { visibleModal: props.visibleModal };
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    if (!!prevProps.visibleModal && !this.props.visibleModal) {
      clearTimeout(this.propagationTimeout);
      this.propagationTimeout = setTimeout(() => this.setState({ visibleModal: null }), TRANSITION_TOTAL_DURATION);
    }

    if (!prevProps.visibleModal && this.props.visibleModal) {
      this.props.logGTMEvent(GTMEvents.AuthOpen);
    }

    if (this.props.visibleModal && prevProps !== this.props.visibleModal) {
      this.handleScrollable();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  getContentRef = () => path(['current', 'targetRef', 'current'])(this.scrollContainerRef);
  propagationTimeout = null;
  scrollContainerRef = React.createRef();
  contentRef = React.createRef();

  handleScrollable = () => {
    if (this.contentRef.current) {
      this.setState({ scrollable: this.contentRef.current.offsetHeight > window.innerHeight });
    }
  };

  handleResize = debounce(this.handleScrollable, 100);

  handleScrollContainerClick = e => {
    if (e.target === this.getContentRef()) {
      this.handleClose();
    }
  };

  handleClose = () => {
    this.props.hide();
    this.props.logGTMEvent(GTMEvents.AuthClose);
  };

  handleSetSubmittedResetPasswordEmail = email => this.setState({ submittedResetPasswordEmail: email });

  handleLoginClick = () => this.props.show(LOGIN_TAB);

  handleRegisterClick = () => this.props.show(REGISTER_TAB);

  handleResetPasswordClick = () => this.props.show(RESET_PASSWORD_TAB);

  renderContent = cond([
    [equals(REGISTER_TAB), () => <RegisterForm onLoginClick={this.handleLoginClick} onClose={this.handleClose} />],
    [
      equals(LOGIN_TAB),
      () => (
        <LoginForm
          onResetPasswordClick={this.handleResetPasswordClick}
          onRegisterClick={this.handleRegisterClick}
          onClose={this.handleClose}
        />
      ),
    ],
    [
      equals(RESET_PASSWORD_TAB),
      () => (
        <ResetPasswordForm
          onSubmitClick={this.handleSetSubmittedResetPasswordEmail}
          onLoginClick={this.handleLoginClick}
          onSignUpClick={this.handleRegisterClick}
          onClose={this.handleClose}
        />
      ),
    ],
    [
      equals(RESET_PASSWORD_TAB_SUCCESS),
      () => (
        <ResetPasswordFormSuccess
          email={this.state.submittedResetPasswordEmail}
          onLoginClick={this.handleLoginClick}
          onSignUpClick={this.handleRegisterClick}
          onClose={this.handleClose}
        />
      ),
    ],
    [T, always(null)],
  ]);

  render = () => (
    <Overlay
      totalDuration={FADE_IN_UP_TRANSITION_TOTAL_DURATION}
      isVisible={!!this.props.visibleModal}
      onHideRequest={this.handleClose}
    >
      <BodyScrollLock ref={this.scrollContainerRef}>
        <ScrollContainer scrollable={this.state.scrollable} onClick={this.handleScrollContainerClick}>
          <Content ref={this.contentRef}>{this.renderContent(this.state.visibleModal)}</Content>
        </ScrollContainer>
      </BodyScrollLock>
    </Overlay>
  );
}

export const AuthOverlay = compose(injectIntl)(AuthOverlayComponent);
